import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@upr-web-primer/storage';
import { StorageModule } from '@upr-web-primer/storage';
import * as i2 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import * as i3 from '@upr-web-primer/locale';
import { merge, split, isEmpty, get, toLower, map, trim, some, find } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

const LOCAL_STORAGE_GEO_KEY = 'USR_SELECTED_RESIDENT_FILTER';
const GEO_COUNTRY_CODE = 'GEO_COUNTRY_CODE';
const GEO_REGION_CODE = 'GEO_REGION_CODE';
const GEO_NON_PRIMARY_RESIDENT_VALUE = 'non-primary';

const geoDefaultOptions = {
    GEOLOCATION_DISABLED: false,
    GEOLOCATION_AKAMAI: true,
    PRIMARY_GEO_REGION: 'US-FL,US-GA',
};
class GeolocationService {
    constructor(localStorage, cookieService, localeService, appDefaults) {
        this.localStorage = localStorage;
        this.cookieService = cookieService;
        this.localeService = localeService;
        this.appDefaults = appDefaults;
        this.geoLocation$ = new BehaviorSubject(this.getGeoLocationObject());
        this.appDefaults = merge(geoDefaultOptions, this.appDefaults);
        this.primaryGeoRegion = this.appDefaults.PRIMARY_GEO_REGION;
    }
    initGeoLocation() {
        if (this.appDefaults.GEOLOCATION_DISABLED) {
            return;
        }
        return this.setGeoLocationAkamai();
    }
    setGeoLocationToNonResident() {
        if (this.appDefaults.GEOLOCATION_DISABLED) {
            return;
        }
        this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, GEO_NON_PRIMARY_RESIDENT_VALUE);
        this.geoLocation$.next(this.getGeoLocationObject());
    }
    setGeoLocationToResident() {
        if (this.appDefaults.GEOLOCATION_DISABLED) {
            return;
        }
        const configRegions = split(this.primaryGeoRegion, ',');
        const location = !!configRegions && configRegions.length > 0
            ? configRegions[0]
            : GEO_NON_PRIMARY_RESIDENT_VALUE;
        this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, location);
        this.geoLocation$.next(this.splitGeoLocationAkamai(location));
    }
    removeGeoLocation() {
        this.localStorage.removeItem(LOCAL_STORAGE_GEO_KEY);
    }
    filterByTcmPersona(collection) {
        if (isEmpty(collection)) {
            return [];
        }
        if (this.appDefaults.GEOLOCATION_DISABLED) {
            return collection;
        }
        return this.filterByTcmPersonaAkamai(collection);
    }
    getPersona(item) {
        return (get(item, 'Persona.KeywordValues.0.Key') ||
            get(item, 'Component.MetadataFields.Persona.KeywordValues.0.Key') ||
            get(item, 'MetadataFields.Persona.KeywordValues.0.Key') ||
            '');
    }
    getGeoCountry() {
        return (this.localStorage.getItem(GEO_COUNTRY_CODE) ||
            this.localeService.getFromStorage().country ||
            '');
    }
    getGeoLocationObject() {
        const geo = this.getGeoLocation();
        return this.splitGeoLocationAkamai(geo);
    }
    getGeoLocation() {
        const value = this.localStorage.getItem(LOCAL_STORAGE_GEO_KEY);
        if (!value || value === 'null') {
            return null;
        }
        return value;
    }
    setGeoLocationAkamai(ignoreExistingGeo) {
        const akamaiCountry = this.cookieService.get(GEO_COUNTRY_CODE) || '';
        const akamaiRegion = this.cookieService.get(GEO_REGION_CODE) || '';
        const geo = this.getGeoLocation();
        if (!!geo && !ignoreExistingGeo) {
            this.localStorage.setItem(GEO_COUNTRY_CODE, akamaiCountry);
            this.geoLocation$.next(this.splitGeoLocationAkamai(geo));
            return;
        }
        if (!akamaiCountry || !akamaiRegion) {
            this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, GEO_NON_PRIMARY_RESIDENT_VALUE);
            this.localStorage.setItem(GEO_COUNTRY_CODE, akamaiCountry || this.localeService.getFromStorage().country);
            this.geoLocation$.next(this.splitGeoLocationAkamai(GEO_NON_PRIMARY_RESIDENT_VALUE));
            return;
        }
        const newGeo = this.determineGeoLocationStatus(akamaiCountry, akamaiRegion);
        this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, newGeo);
        this.localStorage.setItem(GEO_COUNTRY_CODE, akamaiCountry);
        this.geoLocation$.next(this.splitGeoLocationAkamai(newGeo));
    }
    splitGeoLocationAkamai(location) {
        if (!location || location === GEO_NON_PRIMARY_RESIDENT_VALUE) {
            return {
                country: this.getGeoCountry(),
                region: GEO_NON_PRIMARY_RESIDENT_VALUE,
            };
        }
        const parts = split(location, '-');
        return {
            country: parts[0] || '',
            region: parts[1] || '',
        };
    }
    filterByTcmPersonaAkamai(collection) {
        return collection.filter((item) => {
            const componentPersona = this.getPersona(item);
            if (!componentPersona || componentPersona === 'All') {
                return true;
            }
            let hasGeo = !!this.getGeoLocation();
            if (hasGeo && this.getGeoLocation() === GEO_NON_PRIMARY_RESIDENT_VALUE) {
                hasGeo = false;
            }
            if (!hasGeo) {
                return toLower(componentPersona) === 'ous';
            }
            const configRegions = split(this.primaryGeoRegion, ',');
            const regions = map(configRegions, (cr) => {
                let region = '';
                const parts = split(cr, '-');
                if (parts && parts.length == 2) {
                    region = parts[1];
                }
                else {
                    region = cr;
                }
                return toLower(trim(region));
            });
            return some(regions, (r) => {
                return r === toLower(componentPersona);
            });
        });
    }
    determineGeoLocationStatus(country, region) {
        const configRegions = split(this.primaryGeoRegion, ',');
        const selected = find(configRegions, (cr) => {
            return toLower(trim(cr)) === toLower(`${trim(country)}-${trim(region)}`);
        });
        return selected || GEO_NON_PRIMARY_RESIDENT_VALUE;
    }
}
GeolocationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationService, deps: [{ token: i1.LocalStorageService }, { token: i2.CookieService }, { token: i3.LocaleService }, { token: 'AppDefaults' }], target: i0.ɵɵFactoryTarget.Injectable });
GeolocationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.LocalStorageService }, { type: i2.CookieService }, { type: i3.LocaleService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }]; } });

class GeolocationModule {
}
GeolocationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GeolocationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationModule, imports: [CommonModule, HttpClientModule, StorageModule] });
GeolocationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationModule, providers: [CookieService, GeolocationService], imports: [[CommonModule, HttpClientModule, StorageModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GeolocationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, HttpClientModule, StorageModule],
                    providers: [CookieService, GeolocationService]
                }]
        }] });

export { GEO_COUNTRY_CODE, GEO_NON_PRIMARY_RESIDENT_VALUE, GEO_REGION_CODE, GeolocationModule, GeolocationService, LOCAL_STORAGE_GEO_KEY };
